import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Armada SSO AuthContext.
 *
 * Verifies the shared armada_token cookie against this hull's /api/auth/me,
 * which proxies to https://auth.eigooinc.net/api/auth/me server-to-server.
 *
 * States:
 *   status === "loading"      → verifying session
 *   status === "anonymous"    → no / invalid session
 *   status === "user"         → signed in, non-admin
 *   status === "admin"        → signed in and allowed to access the Command Bridge
 */

export const SSO_LOGIN_URL = "https://auth.eigooinc.net/login";
export const SSO_LOGOUT_URL = "https://auth.eigooinc.net/logout";

const AuthContext = createContext({
  status: "loading",
  user: null,
  isAdmin: false,
  refresh: async () => {},
});

export function AuthProvider({ children }) {
  const [status, setStatus] = useState("loading");
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const refresh = async () => {
    try {
      const data = await api.getAuthMe();
      setUser(data.user || null);
      setIsAdmin(Boolean(data.is_admin));
      setStatus(data.is_admin ? "admin" : "user");
    } catch (err) {
      setUser(null);
      setIsAdmin(false);
      setStatus("anonymous");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, isAdmin, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function loginUrlFor(nextHref) {
  const redirect = encodeURIComponent(nextHref || window.location.href);
  return `${SSO_LOGIN_URL}?redirect=${redirect}`;
}

export function logoutUrlFor(nextHref) {
  const redirect = encodeURIComponent(nextHref || `${window.location.origin}/`);
  return `${SSO_LOGOUT_URL}?redirect=${redirect}`;
}

export function AdminRoute({ children }) {
  const { status, user } = useAuth();

  if (status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-gray-400"
        data-testid="admin-route-loading"
      >
        <span className="label-tag">Verifying access…</span>
      </div>
    );
  }

  if (status === "anonymous") {
    // Full redirect so the SSO service can mint the shared cookie.
    window.location.replace(loginUrlFor(window.location.href));
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-gray-400"
        data-testid="admin-route-redirecting"
      >
        <span className="label-tag">Redirecting to Armada SSO…</span>
      </div>
    );
  }

  if (status === "user") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white px-6"
        data-testid="admin-route-forbidden"
      >
        <div className="card-sharp p-10 max-w-lg w-full text-center space-y-4">
          <div className="label-tag gold-text">Access Restricted</div>
          <h1 className="text-3xl font-black tracking-tighter">Command Bridge is Director-Only</h1>
          <p className="text-sm text-gray-400">
            Your Armada session is active
            {user?.email ? ` as ${user.email}` : ""}, but this console is reserved for
            EIGOO Directors.
          </p>
          <a
            href={logoutUrlFor(`${window.location.origin}/`)}
            className="btn-ghost-gold inline-flex items-center gap-2"
            data-testid="admin-route-signout"
          >
            Sign out
          </a>
        </div>
      </div>
    );
  }

  return children;
}
